import React from 'react';

/**
 * Reusable error message component with retry functionality
 * Requirements: 14.5
 * 
 * @param {Object} props - Component props
 * @param {string} props.message - Error message to display
 * @param {Function} props.onRetry - Optional retry callback function
 * @param {string} props.type - Error type: 'error', 'warning', 'info' (default: 'error')
 * @param {boolean} props.showIcon - Whether to show icon (default: true)
 * @param {boolean} props.fullWidth - Whether to take full width (default: false)
 */
const ErrorMessage = ({ 
  message = 'An error occurred',
  onRetry,
  type = 'error',
  showIcon = true,
  fullWidth = false
}) => {
  const typeConfig = {
    error: {
      icon: '⚠️',
      backgroundColor: '#fff3cd',
      borderColor: '#ffeaa7',
      textColor: '#856404',
      iconColor: '#f39c12'
    },
    warning: {
      icon: '⚠️',
      backgroundColor: '#fff3cd',
      borderColor: '#ffeaa7',
      textColor: '#856404',
      iconColor: '#f39c12'
    },
    info: {
      icon: 'ℹ️',
      backgroundColor: '#d1ecf1',
      borderColor: '#bee5eb',
      textColor: '#0c5460',
      iconColor: '#17a2b8'
    },
    network: {
      icon: '🌐',
      backgroundColor: '#f8d7da',
      borderColor: '#f5c6cb',
      textColor: '#721c24',
      iconColor: '#dc3545'
    }
  };

  const config = typeConfig[type] || typeConfig.error;

  // Detect network errors
  const isNetworkError = message.toLowerCase().includes('network') || 
                         message.toLowerCase().includes('connection') ||
                         message.toLowerCase().includes('offline');

  const displayConfig = isNetworkError ? typeConfig.network : config;

  return (
    <div 
      style={{
        ...styles.container,
        backgroundColor: displayConfig.backgroundColor,
        borderColor: displayConfig.borderColor,
        width: fullWidth ? '100%' : 'auto'
      }}
    >
      <div style={styles.content}>
        {showIcon && (
          <span 
            style={{
              ...styles.icon,
              color: displayConfig.iconColor
            }}
          >
            {displayConfig.icon}
          </span>
        )}
        
        <div style={styles.messageContainer}>
          <p 
            style={{
              ...styles.message,
              color: displayConfig.textColor
            }}
          >
            {message}
          </p>
          
          {isNetworkError && (
            <p style={styles.hint}>
              Please check your internet connection and try again.
            </p>
          )}
        </div>
      </div>

      {onRetry && (
        <button
          onClick={onRetry}
          style={styles.retryButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#2980b9';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#3498db';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          <span style={styles.retryIcon}>🔄</span>
          Try Again
        </button>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '20px',
    borderRadius: '12px',
    border: '1px solid',
    margin: '16px 0',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  icon: {
    fontSize: '32px',
    flexShrink: 0,
    lineHeight: 1
  },
  messageContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  message: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '500',
    lineHeight: '1.5'
  },
  hint: {
    margin: 0,
    fontSize: '13px',
    color: '#6c757d',
    fontStyle: 'italic'
  },
  retryButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 6px rgba(52, 152, 219, 0.2)'
  },
  retryIcon: {
    fontSize: '14px'
  }
};

export default ErrorMessage;
